/**
 * Declarative tool-card groups shown on the Deckorate home screen. Data +
 * icon wiring only — no behaviour, no services, no hooks. A card with `nav`
 * is a built feature; without it, it's an unimplemented "Coming soon" tile.
 */

import type { ComponentType } from "react";
import {
  ApplyBrandIcon,
  BrandCheckIcon,
  ColorToolsIcon,
  CommandIcon,
  CompareIcon,
  CreateSlidesIcon,
  DesignHubIcon,
  FormatToolsIcon,
  LogosIcon,
  MapIcon,
  OrgChartIcon,
  ShareIcon,
} from "./deckIcons";

/** Views the parent DeckorateModule can navigate to (excludes "home").
 * "asset-library" is handled specially by the parent — it opens the Asset
 * Library dialog as an overlay rather than swapping the current view (see
 * DeckorateModule's navigate). */
export type DeckorateNav =
  | "apply-brand"
  | "brand-check"
  | "color"
  | "fix-alignment"
  | "format"
  | "infographic"
  | "map"
  | "org-chart"
  | "photo-headshot"
  | "sketch-to-slide"
  | "speaker-notes"
  | "insights"
  | "compare"
  | "create-slide"
  | "share-export"
  | "asset-library";

export interface DeckCard {
  readonly icon: ComponentType;
  readonly title: string;
  readonly description: string;
  /** Present when the card opens a built feature. */
  readonly nav?: DeckorateNav;
  /** Present when the card opens the All-actions command palette instead of a view. */
  readonly action?: "open-palette";
  /** Optional keyboard-shortcut hint shown as a subtle chip. */
  readonly shortcut?: string;
  /** Full-width card (spans both grid columns). */
  readonly wide?: boolean;
}

export interface DeckGroup {
  readonly title: string;
  readonly subtitle: string;
  readonly cards: readonly DeckCard[];
}

/** Keyboard shortcuts for features whose home-ribbon card was removed
 * (decluttered into "All actions" only) but that should still be reachable
 * by their existing shortcut — shortcuts.ts reads DECK_GROUPS for its
 * nav→shortcut map, so a card's removal would otherwise silently drop it too. */
export const HIDDEN_NAV_SHORTCUTS: ReadonlyArray<{
  readonly nav: DeckorateNav;
  readonly shortcut: string;
}> = [
  { nav: "speaker-notes", shortcut: "Ctrl+Alt+S" },
  { nav: "fix-alignment", shortcut: "Ctrl+Alt+A" },
];

/* Section order, card set and copy taken directly from the approved mockup's
   RIBBON_GROUPS — five feature groups plus "Everything". Cards without a `nav`
   (Logos) render as disabled "Coming soon" tiles; every other card here is a
   built feature.

   Deliberately absent, exactly as in the mockup: Convert to Infographic, Photo
   Headshot and Sketch to Slide. All three are selection-driven, so they live on
   the chat action chips and in "All actions" (catalog.ts's AI_LIVE_COMMANDS)
   rather than taking a home-grid slot. */
export const DECK_GROUPS: readonly DeckGroup[] = [
  {
    title: "Create & design",
    subtitle: "Start new content or reuse reviewed layouts",
    cards: [
      {
        icon: CreateSlidesIcon,
        title: "Create Slides",
        description: "From a text prompt or uploaded documents",
        nav: "create-slide",
        shortcut: "Ctrl+Alt+N",
      },
      {
        icon: DesignHubIcon,
        title: "Asset Library",
        description: "Client & Deckorate template library",
        nav: "asset-library",
        shortcut: "Ctrl+Alt+D",
      },
    ],
  },
  {
    title: "Brand & compliance",
    subtitle: "Get the deck on-brand and keep it there",
    cards: [
      {
        icon: ApplyBrandIcon,
        title: "Apply Brand",
        description: "Colors, fonts & formats, slide or deck",
        nav: "apply-brand",
        shortcut: "Ctrl+Alt+B",
      },
      {
        icon: BrandCheckIcon,
        title: "Brand Check",
        description: "Scan & fix brand issues by category",
        nav: "brand-check",
        shortcut: "Ctrl+Alt+K",
      },
    ],
  },
  {
    title: "Shape & colour tools",
    subtitle: "Precision clean-up on selected shapes",
    cards: [
      {
        icon: FormatToolsIcon,
        title: "Format Tools",
        description: "60+ alignment, size, dock & table tools",
        nav: "format",
      },
      {
        icon: ColorToolsIcon,
        title: "Colour Tools",
        description: "Theme palette, replace & non-brand fixes",
        nav: "color",
      },
    ],
  },
  {
    title: "Visual builders",
    subtitle: "Turn data into visuals",
    cards: [
      {
        icon: OrgChartIcon,
        title: "Org Chart",
        description: "People list or CSV → native chart",
        nav: "org-chart",
      },
      {
        icon: MapIcon,
        title: "Plot on Map",
        description: "Locations → markers on a map slide",
        nav: "map",
      },
      {
        icon: LogosIcon,
        title: "Logos",
        description: "Company logos → your slide",
      },
    ],
  },
  {
    title: "Deck utilities",
    subtitle: "Whole-deck operations & output",
    cards: [
      {
        icon: CompareIcon,
        title: "Compare versions",
        description: "Diff an earlier .pptx, revert changes",
        nav: "compare",
      },
      {
        icon: ShareIcon,
        title: "Share & Export",
        description: "PPTX, PDF, email, links",
        nav: "share-export",
      },
    ],
  },
  {
    title: "Everything",
    subtitle: "",
    cards: [
      {
        icon: CommandIcon,
        title: "All actions",
        description: "Fuzzy search every tool · shortcuts listed inside",
        action: "open-palette",
        shortcut: "Ctrl+Shift+K",
        wide: true,
      },
    ],
  },
];
